import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Alert,
  Button,
  Empty,
  Input,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import {
  CashEntry,
  CashHead,
  cashApiErrorMessage,
  projectCashApis,
} from "../../../api/ProjectCash";
import { fmtINR, num } from "../reports/ThreeMConsolidation/threeMShared";
import ProjectScopeSelect from "../shared/ProjectScopeSelect";
import CashEntryFormModal from "./CashEntryFormModal";
import ProjectCashUploadModal from "./ProjectCashUploadModal";
import { CASH_HEADS, cashHeadMeta, fmtCashDate, fmtCashMonth } from "./projectCashShared";

// Project Cash Entries — input screen for the four money heads (RA Bill,
// Escalation = inflow; Site Expenses, Salaries = outflow) per project per
// month that feed the cash-flow report, plus the project's MAX Infra link
// (insurance / EMI / BG charges come from there). One GET loads every head;
// tabs filter client-side.

const ROUTE = "/pms/project/project-cash-entries";

const ProjectCashEntries = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const rawProjectId = stateProjectId ?? params.projectId;
  const projectId = rawProjectId ? Number(rawProjectId) : undefined;

  const [entries, setEntries] = useState<CashEntry[]>([]);
  const [serverProjectName, setServerProjectName] = useState<string>("");
  const [maxInfraId, setMaxInfraId] = useState<string>("");
  const [maxInfraDraft, setMaxInfraDraft] = useState<string>("");
  const [savingLink, setSavingLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeHead, setActiveHead] = useState<CashHead>("RA_BILL");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<CashEntry | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await projectCashApis.getEntries(projectId);
        if (cancelled) return;
        if (res?.status) {
          const payload = res?.data || {};
          setEntries(Array.isArray(payload.entries) ? payload.entries : []);
          setServerProjectName(payload.projectName || "");
          setMaxInfraId(payload.maxInfraProjectId || "");
          setMaxInfraDraft(payload.maxInfraProjectId || "");
        } else {
          const msg = cashApiErrorMessage(res, "Failed to load cash entries");
          setError(msg);
          setEntries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshTick]);

  const projectName = serverProjectName || stateProjectName || "Project";

  const handleProjectChange = (id: number, label?: string) => {
    if (!id || id === projectId) return;
    navigate(`${ROUTE}/${id}`, {
      state: { ...((location.state as any) || {}), id, projectName: label },
    });
  };

  const handleSaveLink = async () => {
    if (!projectId) return;
    setSavingLink(true);
    try {
      const value = maxInfraDraft.trim();
      const res = await projectCashApis.saveMaxInfraLink(projectId, value);
      if (res?.status) {
        message.success(
          res?.data?.message ||
            res?.message ||
            (value ? "MAX Infra project linked" : "MAX Infra link cleared")
        );
        setMaxInfraId(value);
        setMaxInfraDraft(value);
      } else {
        message.error(cashApiErrorMessage(res, "Could not save the MAX Infra link."));
      }
    } finally {
      setSavingLink(false);
    }
  };

  const handleDelete = async (entry: CashEntry) => {
    setDeletingId(entry.cashEntryId);
    try {
      const res = await projectCashApis.deleteEntry(entry.cashEntryId);
      if (res?.status) {
        message.success(res?.data?.message || res?.message || "Entry deleted");
        refresh();
      } else {
        message.error(cashApiErrorMessage(res, "Could not delete the entry."));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const headCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    entries.forEach((e) => {
      counts[e.head] = (counts[e.head] || 0) + 1;
    });
    return counts;
  }, [entries]);

  // Newest month first; within a month newest date, then reference.
  const headRows = useMemo(
    () =>
      entries
        .filter((e) => e.head === activeHead)
        .sort(
          (a, b) =>
            (b.entryMonth || "").localeCompare(a.entryMonth || "") ||
            (b.entryDate || "").localeCompare(a.entryDate || "") ||
            (a.referenceNo || "").localeCompare(b.referenceNo || "")
        ),
    [entries, activeHead]
  );
  const headTotal = useMemo(
    () => headRows.reduce((sum, e) => sum + num(e.amount), 0),
    [headRows]
  );

  const meta = cashHeadMeta(activeHead)!;

  const columns = [
    {
      title: "Month",
      dataIndex: "entryMonth",
      key: "entryMonth",
      width: 120,
      render: (m: string) => fmtCashMonth(m),
    },
    {
      title: meta.referenceLabel,
      dataIndex: "referenceNo",
      key: "referenceNo",
      width: 160,
      render: (v: string) => v || <span className="text-soft">—</span>,
    },
    {
      title: "Date",
      dataIndex: "entryDate",
      key: "entryDate",
      width: 130,
      render: (d: string) => (d ? fmtCashDate(d) : <span className="text-soft">—</span>),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 170,
      align: "right" as const,
      render: (v: string) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtINR(v)}</span>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: { showTitle: false },
      render: (v: string) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            {v}
          </Tooltip>
        ) : (
          <span className="text-soft">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      align: "center" as const,
      render: (_: any, record: CashEntry) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(record);
                setFormOpen(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this entry?"
            description={`${meta.label} · ${fmtCashMonth(record.entryMonth)} · ${fmtINR(
              record.amount
            )}`}
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                loading={deletingId === record.cashEntryId}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const linkDirty = maxInfraDraft.trim() !== maxInfraId;

  return (
    <>
      <Head title="Project Cash Entries" />
      <Content>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>Project Cash Entries</h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectId ? `${projectName} · ` : ""}RA bills, escalation, site
                  expenses and salaries by month — they feed the cash-flow report
                </span>
              </div>
              <Space wrap>
                <ProjectScopeSelect
                  hideOrgMaster
                  value={projectId}
                  onChange={handleProjectChange}
                  placeholder="Select a project"
                  style={{ width: 260 }}
                />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refresh}
                  disabled={!projectId || loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {!projectId ? (
              <Empty
                style={{ marginTop: 80 }}
                description="Select a project to view and enter its cash entries"
              />
            ) : (
              <>
                {/* MAX Infra link */}
                <div
                  style={{
                    border: "1px solid #e5e9f2",
                    borderRadius: 6,
                    padding: "12px 16px",
                    marginBottom: 16,
                    background: "#fafbfd",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <label className="form-label mb-0" style={{ marginRight: 4 }}>
                      <LinkOutlined /> MAX Infra project ID
                    </label>
                    <Input
                      value={maxInfraDraft}
                      onChange={(e) => setMaxInfraDraft(e.target.value)}
                      onPressEnter={() => linkDirty && handleSaveLink()}
                      placeholder="Not linked"
                      maxLength={100}
                      allowClear
                      style={{ width: 240 }}
                      disabled={loading}
                    />
                    <Button
                      type="primary"
                      onClick={handleSaveLink}
                      loading={savingLink}
                      disabled={!linkDirty || loading}
                    >
                      Save
                    </Button>
                    {maxInfraId ? (
                      <Tag color="green">Linked</Tag>
                    ) : (
                      <Tag>Not linked</Tag>
                    )}
                  </div>
                  <div className="text-soft" style={{ fontSize: 12, marginTop: 6 }}>
                    Links this project to its MAX Infra project so insurance, EMIs and
                    BG charges appear in the Control Tower's Inflow &amp; Outflow.
                  </div>
                </div>

                {error ? (
                  <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={error}
                  />
                ) : null}

                <Tabs
                  activeKey={activeHead}
                  onChange={(key) => setActiveHead(key as CashHead)}
                  items={CASH_HEADS.map((h) => ({
                    key: h.code,
                    label: (
                      <span>
                        {h.label}{" "}
                        <span className="text-soft" style={{ fontSize: 12 }}>
                          ({headCounts[h.code] || 0})
                        </span>
                      </span>
                    ),
                  }))}
                  tabBarExtraContent={
                    <Space>
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => setUploadOpen(true)}
                      >
                        Upload Excel
                      </Button>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setEditing(null);
                          setFormOpen(true);
                        }}
                      >
                        Add entry
                      </Button>
                    </Space>
                  }
                />

                {/* Total strip */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 8,
                    padding: "8px 12px",
                    marginBottom: 12,
                    borderRadius: 6,
                    background: meta.flow === "Inflow" ? "#f0f7ff" : "#fff7e6",
                  }}
                >
                  <span>
                    <Tag color={meta.flow === "Inflow" ? "blue" : "orange"}>
                      {meta.flow}
                    </Tag>
                    {meta.label} · {headRows.length} entr{headRows.length === 1 ? "y" : "ies"}
                  </span>
                  <span>
                    Total{" "}
                    <b style={{ fontVariantNumeric: "tabular-nums", color: "#022c57" }}>
                      {fmtINR(headTotal)}
                    </b>
                  </span>
                </div>

                <Table
                  size="small"
                  rowKey="cashEntryId"
                  loading={loading}
                  columns={columns}
                  dataSource={headRows}
                  scroll={{ x: 800 }}
                  pagination={{
                    pageSize: 25,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={`No ${meta.label} entries yet`}
                      />
                    ),
                  }}
                  summary={() =>
                    headRows.length ? (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={3}>
                          <b>Total</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <b style={{ fontVariantNumeric: "tabular-nums" }}>
                            {fmtINR(headTotal)}
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} colSpan={2} />
                      </Table.Summary.Row>
                    ) : null
                  }
                />
              </>
            )}
          </div>
        </Card>
      </Content>

      {projectId ? (
        <>
          <CashEntryFormModal
            open={formOpen}
            onClose={() => {
              setFormOpen(false);
              setEditing(null);
            }}
            onSaved={refresh}
            projectId={projectId}
            head={editing?.head || activeHead}
            entry={editing}
          />
          <ProjectCashUploadModal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            onSuccess={refresh}
            projectId={projectId}
            projectName={projectName}
          />
        </>
      ) : null}
    </>
  );
};

export default ProjectCashEntries;
